import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  CardTitle as DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useInviteUser } from "@/features/clients/hooks/use-clients";
import { useHasRole } from "@/features/clients/hooks/use-has-role";
import type { ClientRole } from "@/features/clients/types";
import { UserPlus } from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "@/features/utils/i18n";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

export const InviteMemberDialog = () => {
  const { t } = useTranslation("members");
  const { company: clientId } = useParams<{ company: string }>();
  const [open, onOpenChange] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<ClientRole>("AGENT");

  const isOwner = useHasRole("OWNER");

  const inviteUser = useInviteUser();

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim() || !clientId) return;

    try {
      await inviteUser.mutateAsync({
        clientId,
        email: inviteEmail,
        role: inviteRole,
      });
      toast.success(t("messages.inviteSent"));
      onOpenChange(false);
      setInviteEmail("");
      setInviteRole("AGENT");
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t("messages.inviteError"),
      );
    }
  };

  if (!isOwner) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button>
          <UserPlus className="h-4 w-4 mr-2" />
          {t("inviteMember")}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <form onSubmit={handleInvite}>
          <DialogHeader>
            <DialogTitle>{t("invite.title")}</DialogTitle>
            <DialogDescription>{t("invite.description")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t("invite.emailAddress")}</Label>
              <Input
                id="email"
                type="email"
                placeholder={t("invite.emailPlaceholder")}
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="role">{t("invite.role")}</Label>
              <Select
                value={inviteRole}
                onValueChange={(value: ClientRole) => setInviteRole(value)}
              >
                <SelectTrigger id="role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="NONE">
                    {t("invite.roleLabels.none")}
                  </SelectItem>
                  <SelectItem value="AGENT">
                    {t("invite.roleLabels.agent")}
                  </SelectItem>
                  <SelectItem value="OWNER">
                    {t("invite.roleLabels.owner")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              {t("invite.cancel")}
            </Button>
            <Button type="submit" disabled={inviteUser.isPending}>
              {inviteUser.isPending ? t("invite.sending") : t("invite.send")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
